/**
 * Реестр ключей телеметрии карты.
 *
 * Пакет телеметрии сам добавляет префикс `browser.microservice.map-search-frontend`,
 * поэтому здесь ключи начинаются сразу с домена.
 *
 * К каждому ключу фасад дописывает разрезы `<deviceType>.<route>` — см. createMapTelemetry.
 * Регион и зум в ключи не добавляем осознанно (решение Q13): на snr такая привязка
 * уже приводила к неинформативным метрикам, а дерево метрик становится неуправляемым.
 */
export const TELEMETRY_KEYS = {
  /**
   * SLI 1: сквозное время от начала навигации до отрисованных пинов.
   * Считается одной метрикой, а не суммой слагаемых — перцентили не складываются.
   */
  mapLoad: 'map.load.duration',
  /** Слагаемое для разбора: сколько от начала навигации заняло всё до готовности карты */
  mapInit: 'map.init.duration',
  /**
   * SLI 2: от окончания взаимодействия с картой до отрисованных обновлённых пинов.
   * К ключу дописывается триггер взаимодействия.
   */
  pinsUpdate: 'map.pins-update.duration',
  /**
   * Сколько из времени SLI 2 пришлось на наш собственный троттлинг до старта запроса.
   * Решение Q3: троттлинг входит в бюджет, но должен быть виден отдельно.
   */
  pinsUpdateDelay: 'map.pins-update.delay',
  /**
   * SLI 6: сколько заняла отрисовка одного тайла с пинами.
   * К ключу дописывается бакет по количеству пинов — по образцу android PinBatchLoadDuration,
   * который тоже разделён по количеству. Поштучного замера на пин в canvas сделать нельзя.
   */
  pinsTileRender: 'pins.tile-render.duration',
  /** SLI 7: пин не удалось отрисовать */
  pinsRenderError: 'pins.render.error',
  /** SLI 9: сколько пинов пришло от бэкенда */
  pinsDelivered: 'pins.delivered',
  /** SLI 9: сколько из них дошло до стора пригодными к отрисовке */
  pinsPrepared: 'pins.prepared',
  /** SLI 8: пин пришёл без данных, без которых его нельзя отрисовать */
  pinsDataInvalid: 'pins.data-invalid',
  /** Слагаемое для разбора: сколько отвечала ручка кластеров */
  clustersRequest: 'clusters.request.duration',
  /** Кластеры доехали до пользователя */
  clustersSucceed: 'clusters.success',
  /** Агрегированный отказ, по нему считается SLO */
  clustersFail: 'clusters.fail',
  /** Сегментация отказа по причине, для скорости разбора */
  clustersFailReason: 'clusters.fail-reason',
  /** Запрос отменён пользователем, в SLI не попадает */
  clustersCancelled: 'clusters.cancelled',
  /** Повторная попытка запроса, ранний сигнал деградации бэкенда */
  clustersRetry: 'clusters.retry',
} as const;

/**
 * Причины отказа при загрузке кластеров.
 *
 * Отмена запроса причиной не является: пользователь просто продолжил двигать карту,
 * такие события считаются отдельным счётчиком и в success/fail не попадают.
 */
export enum EClustersFailReason {
  /** Не дождались ответа */
  Timeout = 'timeout',
  /** Ответ пришёл, но распарсить его не удалось */
  BadPayload = 'badPayload',
  /** Бэкенд явно ответил, что запрос невалиден — например, новый фильтр разошёлся с контрактом */
  BadRequest = 'badRequest',
  /** Прочие 4xx */
  Http4xx = 'http4xx',
  /** Ошибка на стороне бэкенда */
  Http5xx = 'http5xx',
  /** Не смогли классифицировать. Если доля велика — разбираем по логам и уточняем классификацию */
  Unknown = 'unknown',
}

/** Значение разреза, когда источник неизвестен: лучше явная метка, чем пропуск сегмента в ключе */
export const UNKNOWN_DIMENSION = 'unknown';

/**
 * Что вызвало обновление пинов. Нужно, чтобы отличать перемещение карты от смены зума
 * и от применения фильтров: у них разные объёмы догружаемых данных и разные ожидания по скорости.
 */
export enum EPinsUpdateTrigger {
  /** Пользователь подвинул карту */
  Pan = 'pan',
  /** Пользователь изменил масштаб */
  Zoom = 'zoom',
  /** Пользователь применил фильтры */
  Filters = 'filters',
}
